/**
 * Group in-memory map points the same way the map-pins API does.
 * More than 1,000 points in view become grid clusters. At or under that,
 * each point is drawn on its own.
 */

export const MAP_PIN_MODE_MAX_POINTS = 1000;
export const MAP_CLUSTER_MAX = 400;

export type MapViewport = {
  north: number;
  south: number;
  east: number;
  west: number;
  zoom: number;
};

export type MapPoint = {
  lat: number;
  lng: number;
};

export type MapCluster = {
  id: string;
  lat: number;
  lng: number;
  count: number;
  north: number;
  south: number;
  east: number;
  west: number;
};

export type MapPointView<T extends MapPoint> = {
  mode: "pins" | "clusters";
  pins: T[];
  clusters: MapCluster[];
  totalInView: number;
};

export function cellDegreesForZoom(zoom: number): number {
  const safeZoom = Math.min(22, Math.max(0, zoom));
  const meters = 96 * (156543.03392 / 2 ** safeZoom);
  return Math.max(meters / 111_320, 0.00015);
}

export function quantizeViewport(viewport: MapViewport): MapViewport {
  const latSpan = Math.max(viewport.north - viewport.south, 0.01);
  const lngSpan = Math.max(Math.abs(viewport.east - viewport.west), 0.01);
  const latStep = latSpan / 4;
  const lngStep = lngSpan / 4;
  return {
    north: Math.ceil(viewport.north / latStep) * latStep,
    south: Math.floor(viewport.south / latStep) * latStep,
    east: Math.ceil(viewport.east / lngStep) * lngStep,
    west: Math.floor(viewport.west / lngStep) * lngStep,
    zoom: Math.round(viewport.zoom),
  };
}

export function viewportsEqual(
  left: MapViewport | null,
  right: MapViewport | null,
): boolean {
  if (left == null || right == null) return left === right;
  return (
    left.north === right.north &&
    left.south === right.south &&
    left.east === right.east &&
    left.west === right.west &&
    left.zoom === right.zoom
  );
}

function pointInViewport(point: MapPoint, viewport: MapViewport): boolean {
  if (point.lat < viewport.south || point.lat > viewport.north) return false;
  if (viewport.west <= viewport.east) {
    return point.lng >= viewport.west && point.lng <= viewport.east;
  }
  return point.lng >= viewport.west || point.lng <= viewport.east;
}

function bucketPoints<T extends MapPoint>(
  points: T[],
  cell: number,
): MapCluster[] {
  const groups = new Map<
    string,
    {
      latBucket: number;
      lngBucket: number;
      count: number;
      latSum: number;
      lngSum: number;
      north: number;
      south: number;
      east: number;
      west: number;
    }
  >();
  for (const point of points) {
    const latBucket = Math.floor(point.lat / cell);
    const lngBucket = Math.floor(point.lng / cell);
    const id = `${latBucket}:${lngBucket}`;
    const group = groups.get(id);
    if (!group) {
      groups.set(id, {
        latBucket,
        lngBucket,
        count: 1,
        latSum: point.lat,
        lngSum: point.lng,
        north: point.lat,
        south: point.lat,
        east: point.lng,
        west: point.lng,
      });
      continue;
    }
    group.count += 1;
    group.latSum += point.lat;
    group.lngSum += point.lng;
    group.north = Math.max(group.north, point.lat);
    group.south = Math.min(group.south, point.lat);
    group.east = Math.max(group.east, point.lng);
    group.west = Math.min(group.west, point.lng);
  }
  return [...groups.values()].map((group) => ({
    id: `${group.latBucket}:${group.lngBucket}`,
    lat: group.latSum / group.count,
    lng: group.lngSum / group.count,
    count: group.count,
    north: group.north,
    south: group.south,
    east: group.east,
    west: group.west,
  }));
}

export function clusterMapPoints<T extends MapPoint>(
  points: T[],
  viewport: MapViewport | null,
): MapPointView<T> {
  const inView = viewport
    ? points.filter((point) => pointInViewport(point, viewport))
    : points;
  if (inView.length <= MAP_PIN_MODE_MAX_POINTS) {
    return {
      mode: "pins",
      pins: inView,
      clusters: [],
      totalInView: inView.length,
    };
  }
  let cell = cellDegreesForZoom(viewport?.zoom ?? 4);
  let clusters = bucketPoints(inView, cell);
  for (
    let attempt = 0;
    attempt < 8 && clusters.length > MAP_CLUSTER_MAX;
    attempt += 1
  ) {
    cell *= 2;
    clusters = bucketPoints(inView, cell);
  }
  return {
    mode: "clusters",
    pins: [],
    clusters,
    totalInView: inView.length,
  };
}
